import { FormControl, FormGroup, Validators } from '@angular/forms';

import type { MomentInput } from 'moment';

import { ErrorCode } from '@constants/error.constant';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { AccountingPeriod } from '@models/accounting-period';
import { Projection } from '@models/projection';

export type CustomAccountingPeriodLabel = 'période' | 'prévision';

export type CustomAccountingPeriodFormType = {
  endDate: FormControl<string | Date | MomentInput | null>;
  accountingPeriod: FormControl<AccountingPeriod | null>;
};

export class CustomAccountingPeriodForm extends FormGroup<CustomAccountingPeriodFormType> {
  protected customAccountingPeriodLabel: CustomAccountingPeriodLabel = 'période';

  constructor(customAccountingPeriodLabel: CustomAccountingPeriodLabel) {
    super({
      endDate: new FormControl<string | Date | MomentInput | null>(null, [Validators.required]),
      accountingPeriod: new FormControl<AccountingPeriod | null>(null, [Validators.required])
    });

    this.customAccountingPeriodLabel = customAccountingPeriodLabel;
  }

  get endDate(): FormControl<string | Date | MomentInput | null> {
    return this.controls.endDate;
  }

  get endDateErrorMessage(): ErrorMessage | null {
    return FormHelper.composeErrorMessages(this.endDate, [
      { method: 'getEmptyErrorMessage', args: [`La date de fin de ${this.customAccountingPeriodLabel} est requise`] },
      {
        method: 'getDatePickerErrorMessage',
        args: [
          {
            checkMin: true,
            minError: ErrorCode.INVALID_DATE,
            minErrorMessage: `La date doit être supérieure ou égale au #value#`,
            checkMax: true,
            maxError: ErrorCode.INVALID_DATE,
            maxErrorMessage: `La date doit être inférieure ou égale au #value#`
          }
        ]
      }
    ]);
  }

  get accountingPeriod(): FormControl<AccountingPeriod | null> {
    return this.controls.accountingPeriod;
  }

  get accountingPeriodErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(
      this.accountingPeriod,
      `La date de début de ${this.customAccountingPeriodLabel} est requise`
    );
  }

  toProjection(accountingPeriodEndDate: string = this.accountingPeriod.value.endDate): Projection {
    const projection: Projection = new Projection();
    projection.startDate = this.accountingPeriod.value.startDate;
    projection.endDate = DateHelper.format(this.endDate.value);
    projection.accountingPeriodEndDate = accountingPeriodEndDate;
    return projection;
  }
}
